import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isAuthValid, clearAuthData } from '../utils/authUtils';
import { useAuth, usePermission, useAnyPermission, useRole } from '../hooks/useAuth';
import { logout, selectPermissions } from '../store/authSlice';

const ProtectedRoute = ({ 
  children, 
  onLogout,
  permission = null,
  permissions = [],
  requireAll = false,
  role = null,
  allowSuperAdmin = false, // Deprecated: no longer used
  redirectTo = '/dashboard'
}) => {
  const dispatch = useDispatch();
  const { isAuthenticated, refreshAuth } = useAuth();
  const hasPermission = usePermission(permission);
  const hasAnyPermission = useAnyPermission(permissions);
  const hasRole = useRole(role);
  const userPermissions = useSelector(selectPermissions);

  useEffect(() => {
    // Check auth status on component mount
    if (!isAuthValid()) {
      // Clean up localStorage and trigger logout
      clearAuthData();
      dispatch(logout());
      if (onLogout) {
        onLogout();
      }
    } else {
      // Refresh auth state from localStorage
      refreshAuth();
    }
  }, [onLogout, dispatch, refreshAuth]);

  // If not authenticated, redirect to login
  if (!isAuthenticated || !isAuthValid()) {
    return <Navigate to="/login" replace />;
  }

  // Role check - strictly permission-based
  if (role && !hasRole) {
    return <Navigate to={redirectTo} replace />;
  }

  // Single permission check - strictly permission-based
  if (permission && !hasPermission) {
    return <Navigate to={redirectTo} replace />;
  }

  // Multiple permissions check - strictly permission-based
  if (permissions.length > 0) {
    if (requireAll) {
      // Check if user has ALL permissions
      const hasAllPermissions = permissions.every(perm => 
        userPermissions.some(userPerm => userPerm.name === perm)
      );
      if (!hasAllPermissions) {
        return <Navigate to={redirectTo} replace />;
      }
    } else {
      // Check if user has ANY permission
      if (!hasAnyPermission) {
        return <Navigate to={redirectTo} replace />;
      }
    }
  }

  // If authenticated and has required permissions, render the protected component
  return children;
};

export default ProtectedRoute; 